/**
 * Building on the overworld — bridges, islands and houses.
 *
 * A student who has answered ten questions may put things on the map: a plank
 * bridge across a channel, an island raised out of open water, a house on dry
 * land. It is the first feature on this platform where a student changes the
 * world rather than walks it, and there is exactly one rule that makes that
 * safe to allow.
 *
 * **A build may only add walkable ground, and never take any away.**
 *
 * `validateOverworld` proves on every build of the site that every kingdom gate
 * and every isle landing can be walked to from the start, because terrain that
 * strands a kingdom is indistinguishable, from inside the game, from being told
 * you are not good enough for it. That proof is about the *authored* map — and
 * a student who could place an impassable tile would be able to falsify it
 * afterwards, on their own machine, with no validator watching. A child who
 * walls off the Fraction Kingdom by accident has broken the one invariant this
 * package exists to keep, and nothing on screen would explain what happened.
 *
 * So nothing buildable is impassable. A bridge and an island *add* walkable
 * ground where there was water; a house is a drawing on a tile that stays
 * exactly as walkable as it was. Reachability can therefore only ever grow,
 * which means the invariant holds by construction rather than by a check that
 * has to be remembered — and no build needs validating, because there is no
 * build that could do harm.
 *
 * Two consequences worth stating, because both look like omissions:
 *
 * **There is no demolish.** Removing a bridge would be removing walkable
 * ground, which is the one thing forbidden above — and a bridge somebody has
 * come to rely on is worse to remove than to have never built. A misplaced
 * house is a house in an odd spot, which costs a student nothing.
 *
 * **Building costs nothing and pays nothing.** No gold, no XP, no mastery. A
 * build is a drawing, in the same category as a cosmetic (§19, §44): nothing in
 * this codebase reads one for difficulty, recommendations, readiness or access,
 * and a student with a hundred houses has proved nothing about fractions.
 *
 * Pure and clock-free like everything here: `at` is passed in by the caller.
 */

import { recordBuild, type Build, type LearnerProfile } from '@yuan-math/learning';
import { isWalkable, terrainAt, type Position, type Terrain, type TileGrid } from './overworld.ts';

/** The achievement that opens building. Ten questions answered — see `progress.ts`. */
export const BUILD_ACHIEVEMENT_ID = 'ten-problems';

/** How many questions it takes. Stated here so a page can say it in words. */
export const BUILD_UNLOCK_ATTEMPTS = 10;

/** What a student can put on the map. Three kinds, and none is impassable. */
export type BuildKind = 'bridge' | 'island' | 'house';

/** The terrains a build may leave behind. Every one of them walkable. */
export type BuildTerrain = Extract<Terrain, 'bridge' | 'sand' | 'grass'>;

export type BuildDef = {
  kind: BuildKind;
  name: string;
  emoji: string;
  /** One line, written for a student, saying where it may go. */
  hint: string;
  /**
   * The terrain the tile becomes.
   *
   * Deliberately narrower than `Terrain`: only the three walkable kinds a build
   * may produce. That is the invariant at the top of this file, written where
   * the compiler can hold it — adding an impassable terrain to this union
   * fails to typecheck against `CHAR_OF`, so nobody can make a buildable wall
   * without first deleting the reason there isn't one.
   */
  becomes: BuildTerrain;
  /** True where this may be placed. Pure, and a function of the ground alone. */
  allowedOn(world: TileGrid, position: Position): boolean;
};

/** Water with no land under it: what a bridge and an island are placed on. */
const isOpenWater = (world: TileGrid, position: Position): boolean =>
  terrainAt(world, position) === 'water';

export const BUILDS: readonly BuildDef[] = [
  {
    kind: 'bridge',
    name: 'Bridge',
    emoji: '🌉',
    hint: 'On water. Planks across a channel — a shortcut you made yourself.',
    becomes: 'bridge',
    allowedOn: isOpenWater,
  },
  {
    kind: 'island',
    name: 'Island',
    emoji: '🏝️',
    hint: 'On water. Raise a sandbar out of the sea and stand on it.',
    becomes: 'sand',
    allowedOn: isOpenWater,
  },
  {
    kind: 'house',
    name: 'House',
    emoji: '🏠',
    /*
     * On ground that is *already* walkable, and it stays walkable: a house is a
     * drawing on a tile rather than a thing in the way. A buildable wall is the
     * version of this feature that lets a child fence off a kingdom.
     */
    hint: 'On dry land. Somewhere to live, and you can still walk through the door.',
    becomes: 'grass',
    allowedOn: (world, position) => isWalkable(world, position),
  },
];

export const buildDef = (kind: string): BuildDef | undefined =>
  BUILDS.find((def) => def.kind === kind);

/**
 * Whether a student may build at all, and what to tell them if not.
 *
 * The sentence matters as much as the answer. A locked tool that will not say
 * what it wants is asking a child to guess (§0.6), and this codebase already
 * states every kingdom seal as mathematics and every locked cosmetic as its
 * condition. So this reports progress rather than a verdict: "6 of 10
 * questions answered" is a plan, and "locked" is a wall.
 *
 * Read off `stats.totalAttempts` — a record of work, never XP, never gold and
 * never a purchase. The matching `ten-problems` achievement is what makes the
 * same fact visible on the achievements page.
 */
export type BuildAccess = {
  unlocked: boolean;
  /** Questions answered so far, capped at the requirement for display. */
  done: number;
  total: number;
  /** What is left to do, in a student's words. Empty once unlocked. */
  requirement: string;
};

export function buildAccess(profile: LearnerProfile): BuildAccess {
  const answered = profile.stats.totalAttempts;
  const unlocked = answered >= BUILD_UNLOCK_ATTEMPTS;
  const left = Math.max(0, BUILD_UNLOCK_ATTEMPTS - answered);
  return {
    unlocked,
    done: Math.min(answered, BUILD_UNLOCK_ATTEMPTS),
    total: BUILD_UNLOCK_ATTEMPTS,
    requirement: unlocked
      ? ''
      : `Answer ${left} more ${left === 1 ? 'question' : 'questions'} to start building.`,
  };
}

/** Everything the student has built, safe on a profile that has built nothing. */
export const buildsOf = (profile: LearnerProfile): readonly Build[] => profile.rpg?.builds ?? [];

/** The build standing on a tile, if any. */
export const buildAt = (profile: LearnerProfile, position: Position): Build | undefined =>
  buildsOf(profile).find((build) => build.x === position.x && build.y === position.y);

/**
 * Whether this build may go on this tile, and why not if it may not.
 *
 * Every reason is about the *ground* or about the ten questions, and none is
 * about mastery, level, gold or how far through the curriculum a student is.
 */
export type Placement = { ok: true } | { ok: false; reason: string };

export function canBuild(
  world: TileGrid,
  profile: LearnerProfile,
  kind: string,
  position: Position,
): Placement {
  const access = buildAccess(profile);
  if (!access.unlocked) return { ok: false, reason: access.requirement };

  const def = buildDef(kind);
  if (!def) return { ok: false, reason: 'There is nothing of that kind to build.' };

  if (terrainAt(world, position) === undefined) {
    return { ok: false, reason: 'That is off the edge of the world.' };
  }
  if (buildAt(profile, position)) {
    return { ok: false, reason: 'There is already something here.' };
  }
  if (!def.allowedOn(world, position)) {
    return { ok: false, reason: def.hint };
  }
  return { ok: true };
}

/**
 * The map as the student has changed it.
 *
 * A copy of the authored rows with each build's terrain written in, so every
 * function in `overworld.ts` — walking, routing, the reachability search — sees
 * the built world without knowing anything about building. That is the whole
 * integration: `stepFrom` does not need to learn what a bridge is.
 *
 * Since every `becomes` is walkable and the authored map is untouched, the
 * built map's walkable set is always a superset of the authored one. The
 * reachability proof `validateOverworld` runs at build time therefore still
 * holds here, which is the argument this module rests on.
 */
export function builtWorld<T extends TileGrid>(world: T, profile: LearnerProfile): T {
  const builds = buildsOf(profile);
  if (builds.length === 0) return world;

  const rows = world.rows.map((row) => [...row]);
  for (const build of builds) {
    const def = buildDef(build.kind);
    if (!def) continue;
    if (build.y < 0 || build.y >= world.height || build.x < 0 || build.x >= world.width) continue;
    const row = rows[build.y];
    if (row) row[build.x] = CHAR_OF[def.becomes];
  }
  return { ...world, rows: rows.map((row) => row.join('')) };
}

/**
 * Puts a build on the map, or hands the profile back untouched.
 *
 * The one entry point, so every rule above is asked exactly once and no caller
 * can place a house on the sea by reaching for the reducer directly — the same
 * arrangement `claimItem` has with `grantItem`.
 */
export function placeBuild(
  world: TileGrid,
  profile: LearnerProfile,
  kind: string,
  position: Position,
  at: number,
): LearnerProfile {
  if (!canBuild(world, profile, kind, position).ok) return profile;
  return recordBuild(profile, { kind, x: position.x, y: position.y, at });
}

/**
 * The character each buildable terrain is written as.
 *
 * Only the three a build can produce, so a new `Terrain` cannot silently become
 * buildable by being added to the map's legend — it has to be added here too,
 * and anyone adding an impassable one has to read the note at the top of this
 * file first.
 */
const CHAR_OF: Record<BuildTerrain, string> = {
  bridge: '=',
  sand: ',',
  grass: '.',
};
